/* eslint-disable import/default */
import get from 'lodash.get';
import merge from 'lodash.merge';
/* eslint-enable import/default */

import { warnWithTrace } from '../helpers/logger';
import { themeSet } from './example-theme-definitions';
import { createCSSVarName } from './create-css-var-name';
import { injectDesignTokensOption } from './inject-design-tokens-option';
/**
 * TypeGuard for type `ThemeName`
 *
 * @param prop Some arbitrary component property
 */

function isThemeName(prop) {
  return typeof prop === 'string';
}

function getDesignTokenValue() {
  var propertyPath = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var optionsOverride = arguments.length > 1 ? arguments[1] : undefined;

  if (!propertyPath) {
    warnWithTrace("Design token value could not be found: ".concat(propertyPath));
    return undefined;
  }

  var options = injectDesignTokensOption(optionsOverride);
  var designTokenValue = get(options.tokens, propertyPath); // array values, return css var instead
  // the assumption is that every array is a dynamic designToken

  if (Array.isArray(designTokenValue)) {
    // @todo: we need to add a fallback for browsers that cannot handle css vars
    return "var(".concat(createCSSVarName(propertyPath), ")");
  }

  return typeof designTokenValue === 'string' ? designTokenValue : undefined;
}

function resolveDesignTokenValues(theme) {
  var resolvedTheme = {};
  Object.entries(theme).forEach(function (entry) {
    var key = entry[0];
    var value = entry[1];

    if (value == null) {
      return;
    }

    if (typeof value === 'object') {
      resolvedTheme[key] = resolveDesignTokenValues(value);
      return;
    }

    resolvedTheme[key] = getDesignTokenValue(value);
  });
  return resolvedTheme;
}
/**
 * Get the current theme form a tree of ThemeProviders
 *
 * Components may be wrapped in multiple ThemeProviders. This function returns the last used (nearest)
 * theme and merges in any new theme or theme properties.
 *
 * @param currentTheme The nearest accessible theme, defaults to global `ngw.ui.theme.main`
 * @param newTheme The new theme or theme partial passed in to the ThemeProvider via props
 */


function getUpdatedTheme(currentTheme, newTheme) {
  var updatedTheme = isThemeName(newTheme) ? themeSet[newTheme] : newTheme;
  var consolidatedTheme = merge(resolveDesignTokenValues(themeSet.main), currentTheme, updatedTheme ? resolveDesignTokenValues(updatedTheme) : {});
  return consolidatedTheme;
}

var mainDesignTokenValues;
/**
 * Memoized version of getUpdatedTheme to avoid recomputing the same theme over and over again.
 * We use a WeakMap as data storage. This has the upside that we can use the currentTheme objects,
 * which are most likely bigger, as map keys. As map value, we use a plain JS object with
 * the stringified newTheme as key and the computed, resolved theme as value.
 *
 * @param currentTheme The nearest accessible theme, defaults to global `ngw.ui.theme.main`
 * @param newTheme The new theme or theme partial passed in to the ThemeProvider via props
 */

var themeDesignTokenValues = new WeakMap();
export function getTheme(currentTheme, newTheme) {
  // If there is neither a currentTheme nor a newTheme, just return the mainTheme
  if (currentTheme == null && newTheme == null) {
    if (!mainDesignTokenValues) {
      mainDesignTokenValues = resolveDesignTokenValues(themeSet.main);
    }

    return mainDesignTokenValues;
  }

  var newThemeID = JSON.stringify(newTheme);
  var safeCurrentTheme = currentTheme || {}; // If currentTheme is not cached, cache it with empty object

  if (themeDesignTokenValues.get(safeCurrentTheme) == null) {
    themeDesignTokenValues.set(safeCurrentTheme, {});
  } // If given, return memoized version of currentTheme-newTheme pair


  if (themeDesignTokenValues.get(safeCurrentTheme)[newThemeID]) {
    return themeDesignTokenValues.get(safeCurrentTheme)[newThemeID];
  }

  var consolidatedTheme = getUpdatedTheme(safeCurrentTheme, newTheme); // memoize calculated theme

  themeDesignTokenValues.get(safeCurrentTheme)[newThemeID] = consolidatedTheme;
  return consolidatedTheme;
}